/**
 * FlowBuilderPage — the Messaging → Flows library editor. Thin wrapper around
 * the shared EventFlowBuilder (canvas + inspector), adding Screen chrome,
 * library load (flowId param), and Save / Discard to the flow library.
 * The identical designer is embedded in the event wizard's Messaging step.
 */
import { useState, useEffect, type CSSProperties } from "react";
import { Btn, Icon, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import { fbDefaultFlow, fbMigrateFlow, fbValidate, type FBFlow } from "./fb-model";
import { useFlows, type SeedFlow } from "../messaging/useFlows";
import { EventFlowBuilder } from "./EventFlowBuilder";
import { useT, t as i18nT } from "../../lib/i18n";

export default function FlowBuilderPage() {
  const t = useT();
  const flowsStore = useFlows();
  const nav = useNav();
  const readOnly = !can("flows.write");
  const flowId = (nav.params?.id ?? nav.params?.flowId) as string | undefined;
  const [flow, setFlow] = useState<FBFlow>(() => fbDefaultFlow());
  const [flowMeta, setFlowMeta] = useState({ id: "", name: t("builder.flowBuilderPage.newFlow", "New flow"), eventKind: "", desc: "" });
  const [dirty, setDirty] = useState(false);

  const flowsLoading = flowsStore.loading;
  const loadFromLibrary = () => {
    if (!flowId) return;
    const f = flowsStore.get(flowId);
    if (!f) return;
    setFlow(fbMigrateFlow({ trigger: f.trigger, steps: JSON.parse(JSON.stringify(f.steps)) }));
    setFlowMeta({ id: f.id, name: f.name || "Untitled flow", eventKind: f.eventKind, desc: f.desc });
    setDirty(false);
  };
  useEffect(loadFromLibrary, [flowId, flowsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFlow = (next: FBFlow) => { setFlow(next); setDirty(true); };

  // UIR-20 (Voyage #216): await the client write; success state only on
  // resolution — a rejected write keeps the form dirty and shows the API error.
  const save = async () => {
    // Flow-audit item 2: a flow with structural issues (paths that never end,
    // empty branches, unattached messages/surveys) cannot be saved to the
    // library — the canvas's issues chip lists each one.
    const issues = fbValidate(flow);
    if (issues.length) {
      toast({ tone: "danger", title: t("builder.flowBuilderPage.cantSaveYet", "Can't save yet"), subtitle: `${issues.length === 1 ? "1 issue" : issues.length + " issues"} to resolve — see the warning chip above the canvas.` });
      return;
    }
    try {
      const saved = await flowsStore.upsert({
        id: flowMeta.id || `flow_${Date.now().toString(36)}`,
        name: flowMeta.name || "Untitled flow",
        eventKind: flowMeta.eventKind || "invite",
        desc: flowMeta.desc, trigger: flow.trigger, steps: flow.steps,
      } as SeedFlow);
      setFlowMeta((m) => ({ ...m, id: saved.id }));
      setDirty(false);
      toast({ tone: "success", title: t("builder.flowBuilderPage.flowSaved", "Flow saved"), subtitle: i18nT("builder.flowBuilderPage.flowmetaNameSavedToLibrary.icu", { value0: flowMeta.name }) });
    } catch (e) {
      toast({ tone: "danger", title: t("builder.flowBuilderPage.couldnTSaveFlow", "Couldn't save flow"), subtitle: e instanceof Error ? e.message : String(e) });
    }
  };
  const discard = () => { loadFromLibrary(); toast({ tone: "info", title: t("builder.flowBuilderPage.changesDiscarded", "Changes discarded") }); };

  const wrap: CSSProperties = { flex: 1, display: "flex", minHeight: 0, margin: "0 24px 24px" };

  return (
    <Screen rail="messaging">
      <PageHeader
        title={readOnly
          ? (flowMeta.name || t("builder.flowBuilderPage.untitledFlow", "Untitled flow"))
          : <input value={flowMeta.name} placeholder={t("builder.flowBuilderPage.untitledFlow", "Untitled flow")} aria-label={t("builder.flowBuilderPage.flowName", "Flow name")}
              size={Math.max(24, (flowMeta.name || "").length + 2)}
              onChange={(e) => { setFlowMeta((m) => ({ ...m, name: e.target.value })); setDirty(true); }}
              style={{ font: "inherit", color: "inherit", border: "none", borderBottom: `1px dashed ${T.n4}`, background: "transparent", outline: "none", padding: "0 2px 2px", maxWidth: "100%", fontFamily: T.font }} />}
        subtitle={flowMeta.eventKind || flowMeta.desc ? `${flowMeta.eventKind}${flowMeta.eventKind && flowMeta.desc ? " · " : ""}${flowMeta.desc}` : (readOnly ? undefined : t("builder.flowBuilderPage.clickTheTitleToRenameThisFlow", "Click the title to rename this flow"))}
        breadcrumb={<span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("messaging.flows")}><Icon.arrow dir="left" size={11} /> {t("builder.flowBuilderPage.flows", "Flows")}</span>}
        actions={<>
          {dirty && !readOnly ? <Btn variant="secondary" onClick={discard}>{t("builder.flowBuilderPage.discard", "Discard")}</Btn> : null}
          {!readOnly ? <Btn onClick={save}>{t("builder.flowBuilderPage.save", "Save")}</Btn> : null}
        </>}
      />
      <div style={wrap}>
        <EventFlowBuilder flow={flow} onFlow={onFlow} readOnly={readOnly} height="100%" />
      </div>
    </Screen>
  );
}
